import 'react-app-polyfill/ie11';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import { CookiesProvider } from 'react-cookie';
import { ThemeProvider } from '@mui/material/styles';

import makeFinalTheme from 'new/theme';
import configureStore from './js/stores/configureStore';

import {
  getPagingData,
  startAccessControlFromLocalStore,
  startSessionFromLocalStore,
} from './js/actions';
import { ROUTE_REDESIGN } from './js/constants/pathnames';
import AnalyticsUtils from './js/lib/AnalyticsUtils';
import Main from './new/Main';
import RoutePrefixContext from './new/contexts/RoutePrefixContext';
import { ROUTE_CONNECT } from './new/constants/pathnames';

import './styles/theme/theme.scss';

import './styles/nexla.css';
import './js/components/elements/schema-editor/schema-browser/SchemaBrowserTable.css';
import './js/components/elements/schema-editor/schema-inspector/SchemaInspector.css';
import './js/components/elements/schema-editor/schema-output/attributes/SharedAttributesTable.css';
import { ApiMocks } from 'new/apiMocks/ApiMocks';
import { AppContextProvider } from 'new/contexts/AppContextProvider';
import getUserConfirmation from 'js/components/route_utils/getUserConfirmation';
import { registerTransformDSL } from 'js/constants/monaco-transform-dsl';
import { loader } from '@monaco-editor/react';
import * as monaco from 'monaco-editor';
import { preloadMonaco } from 'js/utils/preloadMonaco';

// Configure @monaco-editor/react to use the same bundled Monaco instance
// that TransformDSLEditor uses. This eliminates CSS variable conflicts
// between two separate Monaco instances.
loader.config({ monaco });

// Register DSL language on bundled Monaco (used by both TransformDSLEditor and MonacoCodeEditor now)
registerTransformDSL();

// Cmd/Ctrl+Enter is bound by default to "insert line after" in every Monaco
// editor, but this app repurposes that chord everywhere as "run rules"
// (ConfigureRecipeHeader documents it as the run shortcut; ConfigureRecipe
// has a window-level keydown listener that triggers the run action). Disabling
// just that one default action here -- once, globally, via Monaco's public
// keybinding API -- lets the keystroke bubble up to that listener from any
// editor, instead of registering a run command per editor instance, which
// would leak across editors since they all share this one Monaco module (see
// TransformDSLEditor.tsx for how that leak manifests). Removing only
// '-editor.action.insertLineAfter' (rather than nulling the whole chord)
// keeps every other Cmd/Ctrl+Enter default bound -- e.g. "Replace All" in the
// Find widget and accepting a peek-references/code-action preview.
monaco.editor.addKeybindingRule({
  keybinding: monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter,
  command: '-editor.action.insertLineAfter',
});

AnalyticsUtils.initLogRocket();
AnalyticsUtils.initPosthog();
export const store = configureStore();

store.dispatch(startSessionFromLocalStore());
store.dispatch(startAccessControlFromLocalStore());
store.dispatch(getPagingData());

const container = document.getElementById('app-container');
export const root = createRoot(container);

root.render(
  <ThemeProvider theme={makeFinalTheme()}>
    <CookiesProvider>
      <Provider store={store}>
        <Router getUserConfirmation={getUserConfirmation}>
          <Switch>
            <Route path={ROUTE_REDESIGN}>
              <ApiMocks>
                <RoutePrefixContext.Provider value={`${ROUTE_REDESIGN}${ROUTE_CONNECT}`}>
                  <AppContextProvider>
                    <Main />
                  </AppContextProvider>
                </RoutePrefixContext.Provider>
              </ApiMocks>
            </Route>
          </Switch>
        </Router>
      </Provider>
    </CookiesProvider>
  </ThemeProvider>,
);

// Preload Monaco editor in the background after initial render
// Uses requestIdleCallback to avoid blocking the main thread
preloadMonaco();
